(() => {
  if (customElements.get('gsn-gallery')) return;
  function readData(node) {
    try { return JSON.parse(node.querySelector('[data-gsn-data]')?.textContent || '[]'); }
    catch (error) { console.warn('GS gallery data could not be read', error); return []; }
  }
  function validImage(src) {
    if (!src) return Promise.resolve(false);
    // Browser HTTP caching is sufficient. Never cache a transient failure permanently.
    return new Promise(resolve => {
      const img = new Image();
      const timer = setTimeout(() => resolve(false), 10000);
      img.onload = () => { clearTimeout(timer); resolve(img.naturalWidth > 0); };
      img.onerror = () => { clearTimeout(timer); resolve(false); };
      img.src = src;
    });
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  async function renderGallery(target, record) {
    const ticket = {};
    target.gsnTicket = ticket;
    target.gsnFailed = false;
    const loading = element('p', 'gsn-loading', 'Bild wird geladen …');
    loading.setAttribute('role', 'status');
    target.replaceChildren(loading);
    const sources = Array.isArray(record?.images) ? record.images.filter(i => i.src) : [];
    // Load only the first usable display image; all other full views wait for selection.
    let first = 0;
    while (first < sources.length) {
      if (await validImage(sources[first].display || sources[first].src)) break;
      if (target.gsnTicket !== ticket || !target.isConnected) return false;
      first += 1;
    }
    if (target.gsnTicket !== ticket || !target.isConnected) return false;
    const images = sources.slice(first);
    target.replaceChildren();
    if (!images.length) {
      target.gsnFailed = true;
      target.append(element('p', '', 'Bild momentan nicht verfügbar.'));
      return false;
    }
    const wrap = element('div', 'gsn-view');
    const main = element('button', 'gsn-view__main');
    main.type = 'button';
    main.setAttribute('aria-label', 'Bild vergrößern');
    main.setAttribute('aria-pressed', 'false');
    const img = element('img');
    img.decoding = 'async';
    main.append(img);
    const caption = element('p', 'gsn-view__caption');
    caption.setAttribute('aria-live', 'polite');
    const thumbs = element('div', 'gsn-thumbs');
    thumbs.setAttribute('role', 'group');
    thumbs.setAttribute('aria-label', 'Produktansichten');
    let active = 0;
    const failed = new Set();
    const availableIndices = () => images.map((_, index) => index).filter(index => !failed.has(index));
    const select = index => {
      if (failed.has(index)) return;
      active = index;
      main.classList.remove('is-zoomed');
      main.setAttribute('aria-pressed', 'false');
      main.setAttribute('aria-label', 'Bild vergrößern');
      img.hidden = false;
      main.setAttribute('aria-busy', 'true');
      img.src = images[index].display || images[index].src;
      img.alt = record.title + ' · ' + record.color + ' – ' + images[index].label;
      const available = availableIndices();
      caption.textContent = images[index].label + ' · ' + (available.indexOf(index) + 1) + ' / ' + available.length;
      [...thumbs.children].forEach((button, i) => button.setAttribute('aria-current', String(index === i)));
    };
    images.forEach((source, i) => {
      const button = element('button');
      button.type = 'button';
      button.setAttribute('aria-label', source.label + ' anzeigen');
      const thumb = element('img');
      let thumbSource = source.thumb || source.display || source.src;
      const verifiedThumb = typeof source.thumb === 'string'
        ? source.thumb.match(/^((?:https:)?\/\/www\.greenstitches\.de\/cdn\/shop\/files\/gsn-hoodie-black-original-(?:front|back))_160x\.jpg(\?[^#]*)?(#.*)?$/) : null;
      if (verifiedThumb) {
        const query = verifiedThumb[2] || '';
        const parameters = new URLSearchParams(query);
        if (!parameters.has('width') && !parameters.has('format')) {
          thumbSource = verifiedThumb[1] + '.jpg'
            + (query ? query + '&' : '?') + 'width=160&format=pjpg' + (verifiedThumb[3] || '');
        }
      }
      thumb.src = thumbSource;
      thumb.alt = '';
      thumb.loading = 'lazy';
      button.append(thumb);
      button.addEventListener('click', () => select(i));
      button.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const available = availableIndices();
        if (!available.length) return;
        const position = available.indexOf(active);
        const next = event.key === 'Home' ? available[0] : event.key === 'End' ? available[available.length - 1] :
          available[(position + (event.key === 'ArrowRight' ? 1 : -1) + available.length) % available.length];
        select(next);
        thumbs.children[next].focus();
      });
      thumbs.append(button);
    });
    img.addEventListener('load', () => main.removeAttribute('aria-busy'));
    img.addEventListener('error', () => {
      main.removeAttribute('aria-busy');
      failed.add(active);
      thumbs.children[active].hidden = true;
      const next = images.findIndex((_, index) => !failed.has(index));
      if (next >= 0) select(next);
      else { target.gsnFailed = true; img.hidden = true; main.disabled = true; caption.textContent = 'Bild momentan nicht verfügbar.'; }
    });
    main.addEventListener('click', () => {
      const zoomed = main.classList.toggle('is-zoomed');
      main.setAttribute('aria-pressed', String(zoomed));
      main.setAttribute('aria-label', zoomed ? 'Bild verkleinern' : 'Bild vergrößern');
      img.src = zoomed ? images[active].src : (images[active].display || images[active].src);
    });
    wrap.append(main, caption, thumbs);
    target.append(wrap);
    select(0);
    return true;
  }
  class GSNProductGallery extends HTMLElement {
    connectedCallback() {
      this.records = readData(this);
      this.info = this.closest('product-info');
      this.wrapper = this.closest('.product__media-wrapper');
      this.view = this.querySelector('[data-gsn-view]');
      this.change = event => {
        if (event.target.matches('input[name="id"]')) this.selectVariant(event.target.value || this.info?.dataset.gsnDisplayVariant);
      };
      this.info?.addEventListener('change', this.change);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          const data = event?.data;
          if (String(data?.sectionId) === String(this.dataset.section)) this.selectVariant(data?.variant?.id || this.info?.dataset.gsnDisplayVariant);
        });
      }
      this.selectVariant(this.info?.dataset.gsnSizeRequired === 'true' ? this.info.dataset.gsnDisplayVariant : this.dataset.variant);
    }
    disconnectedCallback() {
      this.unsubscribe?.();
      this.info?.removeEventListener('change', this.change);
      this.selectionTicket = null;
      this.recordSlug = null;
      this.loading = false;
      if (this.view) this.view.gsnTicket = null;
      this.wrapper?.classList.remove('gsn-has-gallery');
    }
    async selectVariant(id) {
      const record = this.records.find(row => row.variants.some(v => String(v.id) === String(id)));
      if (record && record.slug === this.recordSlug && !this.view?.gsnFailed && (this.loading || !this.hidden)) return;
      const ticket = {};
      this.selectionTicket = ticket;
      this.recordSlug = record?.slug || null;
      this.loading = Boolean(record && this.view);
      if (this.view) this.view.gsnTicket = null;
      // Restore Dawn for unknown colours and incomplete uploads; never display another colour.
      this.hidden = true;
      this.wrapper?.classList.remove('gsn-has-gallery');
      if (!record || !this.view) return;
      const visible = await renderGallery(this.view, record);
      if (this.selectionTicket !== ticket || !this.isConnected) return;
      this.loading = false;
      this.hidden = !visible;
      this.wrapper?.classList.toggle('gsn-has-gallery', visible);
    }
  }
  const catalogueFamilies = new Set(['all', 'hoodie', 'zip', 'jogger', 'shirts', 'jackets']);
  const normalizeSearch = value => String(value || '').toLowerCase().replace(/ß/g, 'ss')
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/[-,]/g, ' ').trim();
  const queryFromURL = () => new URL(location.href).searchParams.get('q') || '';
  const familyFromURL = () => {
    const value = new URL(location.href).searchParams.get('family');
    return catalogueFamilies.has(value) ? value : 'all';
  };
  class GSNCatalog extends HTMLElement {
    connectedCallback() {
      this.records = readData(this).filter(row => !row.handle || row.variants.length > 0);
      this.grid = this.querySelector('[data-grid]');
      this.dialog = this.querySelector('dialog');
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        this.querySelector('[data-dialog-gallery]').gsnTicket = null;
        if (this.opener?.isConnected) this.opener.focus();
      });
      this.searchForm = this.querySelector('[data-gsn-search]');
      this.searchInput = this.searchForm?.querySelector('[name="q"]');
      if (this.searchInput) {
        this.searchInput.value = queryFromURL();
        this.onSearchInput = () => this.filter(familyFromURL(), false, this.searchInput.value);
        this.onSearchSubmit = event => {
          event.preventDefault();
          this.filter(familyFromURL(), true, this.searchInput.value);
        };
        this.searchInput.addEventListener('input', this.onSearchInput);
        this.searchForm.addEventListener('submit', this.onSearchSubmit);
      }
      const filters = this.querySelector('.gsn-filters');
      filters.replaceChildren();
      [['all', 'Alle'], ['hoodie', 'Hoodies'], ['zip', 'Zipper'], ['jogger', 'Jogger'], ['shirts', 'T-Shirts'], ['jackets', 'Jacken']]
        .filter(([key]) => key === 'all' || this.records.some(r => r.family === key))
        .forEach(([key, label]) => {
          const button = element('button', '', label);
          button.type = 'button';
          button.dataset.filter = key;
          button.addEventListener('click', () => this.filter(key, true));
          filters.append(button);
        });
      this.onHistory = () => {
        if (this.searchInput) this.searchInput.value = queryFromURL();
        this.filter(familyFromURL());
      };
      window.addEventListener('popstate', this.onHistory);
      this.filter(familyFromURL());
    }
    disconnectedCallback() {
      window.removeEventListener('popstate', this.onHistory);
      this.searchInput?.removeEventListener('input', this.onSearchInput);
      this.searchForm?.removeEventListener('submit', this.onSearchSubmit);
    }
    filter(family, userInitiated = false, query = this.searchInput?.value ?? queryFromURL()) {
      if (!catalogueFamilies.has(family)) family = 'all';
      if (userInitiated) {
        const url = new URL(location.href);
        const before = url.href;
        url.searchParams.set('family', family);
        if (query.trim()) url.searchParams.set('q', query.trim());
        else url.searchParams.delete('q');
        if (url.href !== before) {
          history.pushState(null, '', url.pathname + url.search + url.hash);
        }
      }
      this.querySelectorAll('[data-filter]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.filter === family)));
      const words = normalizeSearch(query).split(/\s+/).filter(Boolean);
      const rows = this.records.filter(r => (family === 'all' || r.family === family)
        && words.every(word => normalizeSearch(r.searchText || `${r.title} ${r.color}`).includes(word)));
      this.grid.replaceChildren();
      rows.forEach(row => this.card(row));
      this.querySelector('[data-count]').textContent = rows.length + (rows.length === 1 ? ' Ansicht' : ' Ansichten');
      this.querySelector('[data-empty]').hidden = rows.length > 0;
    }
    card(row) {
      const card = this.querySelector('[data-gsn-card]').content.firstElementChild.cloneNode(true);
      card.dataset.slug = row.slug;
      card.querySelector('[data-title]').textContent = row.title;
      card.querySelector('[data-color]').textContent = row.color;
      card.querySelectorAll('[data-photo],[data-open]').forEach(button => {
        button.setAttribute('aria-label', `Fotos ansehen: ${row.title} · ${row.color}`);
      });
      const variants = row.variants.every(v => v.price > 0) ? row.variants : [];
      const available = variants.filter(v => v.available);
      const chosen = (available.length ? available : variants).reduce((best, v) => !best || v.price < best.price ? v : best, null);
      const price = card.querySelector('[data-price]');
      const shop = card.querySelector('[data-shop]');
      shop.setAttribute('aria-label', `Größe wählen: ${row.title} · ${row.color}`);
      // Zero prices are not free-product claims. Keep unresolved records photo-only.
      if (chosen && row.url) {
        const varies = variants.some(v => v.price !== chosen.price);
        price.textContent = (varies ? 'Ab ' : '') + chosen.money;
        if (available.length) {
          shop.href = row.handle === 'hoodie'
            ? row.url + '?gsn_color=' + encodeURIComponent(row.color)
            : row.url + '?variant=' + chosen.id;
        } else {
          shop.hidden = true;
          price.textContent += ' · Ausverkauft';
        }
      } else {
        price.textContent = 'Derzeit nicht erhältlich';
        shop.hidden = true;
      }
      const open = event => {
        this.opener = event.currentTarget;
        this.querySelector('[data-dialog-title]').textContent = row.title + ' · ' + row.color;
        const target = this.querySelector('[data-dialog-gallery]');
        target.replaceChildren();
        this.dialog.showModal();
        renderGallery(target, row);
      };
      card.querySelector('[data-open]').addEventListener('click', open);
      card.querySelector('[data-photo]').addEventListener('click', open);
      const img = card.querySelector('img');
      const photo = card.querySelector('[data-photo]');
      const primaryAlt = row.title + ' · ' + row.color;
      img.alt = primaryAlt;
      let index = 0;
      let primarySrc = '';
      let previewing = false;
      let hoverTicket = 0;
      const restore = () => {
        hoverTicket += 1;
        if (!previewing) return;
        previewing = false;
        img.src = primarySrc;
        img.alt = primaryAlt;
      };
      const next = () => {
        if (previewing) { restore(); return; }
        const source = row.images[index++];
        if (!source) { img.hidden = true; card.querySelector('[data-no-photo]').hidden = false; return; }
        primarySrc = source.card || source.display || source.src;
        img.src = primarySrc;
      };
      const secondary = row.images[1];
      const secondarySrc = secondary && (secondary.card || secondary.display || secondary.src);
      photo.addEventListener('pointerenter', async event => {
        if (event.pointerType !== 'mouse' || !matchMedia('(any-hover: hover)').matches ||
            !secondarySrc || secondarySrc === primarySrc || img.hidden) return;
        const ticket = ++hoverTicket;
        const ready = await validImage(secondarySrc);
        if (!ready || ticket !== hoverTicket || !photo.isConnected || img.hidden ||
            secondarySrc === primarySrc) return;
        previewing = true;
        img.src = secondarySrc;
        img.alt = primaryAlt + ' – ' + secondary.label;
      });
      photo.addEventListener('pointerleave', restore);
      photo.addEventListener('pointercancel', restore);
      photo.addEventListener('click', restore);
      img.addEventListener('error', next);
      this.grid.append(card);
      next();
    }
  }
  customElements.define('gsn-gallery', GSNProductGallery);
  customElements.define('gsn-catalog', GSNCatalog);
})();
